import React, { useState, useEffect } from 'react';
import { X, Sparkles, MessageSquare, Image as ImageIcon, History, Brain, ListTodo, Grid, Zap, ShieldCheck, Code } from 'lucide-react';
import { Button } from './ui/button';
import { cn, noTextSelect } from '@/lib/utils';
import { useTypewriter } from '@/hooks/useTypewriter';

import { useTheme } from '@/contexts/ThemeContext';

interface PricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    isDarkMode?: boolean;
    planTier?: string;
    limitReachedMessage?: string;
}

export const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose, isDarkMode: isDarkModeProp, planTier = 'free', limitReachedMessage }) => {
    const { theme } = useTheme();
    const isDarkMode = isDarkModeProp ?? (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches));
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');
    const [typewriterKey, setTypewriterKey] = useState(0);

    // Restart typewriter animation when modal opens
    useEffect(() => {
        if (isOpen) {
            setTypewriterKey(prev => prev + 1);
        }
    }, [isOpen]);

    const proSubtextTyped = useTypewriter('Do more with smarter AI', { speedMs: 40, startDelayMs: 120 });

    if (!isOpen) return null;

    const overlayBg = isDarkMode ? 'bg-black/80' : 'bg-black/40';
    const modalBg = isDarkMode ? 'bg-[#18181b] border-white/10' : 'bg-white border-gray-200';
    const toggleBg = isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-200 border-gray-300';
    const toggleActive = isDarkMode ? 'bg-white text-black' : 'bg-white text-black shadow-sm';
    const toggleInactive = isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900';
    const closeBtn = isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900';
    const scrollThumb = isDarkMode ? '[&::-webkit-scrollbar-thumb]:bg-white/10 hover:[&::-webkit-scrollbar-thumb]:bg-white/20' : '[&::-webkit-scrollbar-thumb]:bg-black/10 hover:[&::-webkit-scrollbar-thumb]:bg-black/20';

    const normalizedTier = String(planTier || 'free').toLowerCase();
    const isPro = normalizedTier === 'pro' || normalizedTier === 'enterprise' || normalizedTier === 'starter';

    const plans = [
        {
            name: "Pro",
            monthlyPrice: "399",
            annualPrice: "3990",
            oldMonthlyPrice: "999",
            oldAnnualPrice: "8999",
            specialOffer: true,
            subtext: "Do more with smarter AI",
            btnText: "Upgrade to Pro",
            btnVariant: "primary" as const,
            features: [
                { icon: Sparkles, text: "Go deep on harder questions" },
                { icon: MessageSquare, text: "Chat longer and upload more content" },
                { icon: ImageIcon, text: "Make realistic images for your projects" },
                { icon: Brain, text: "Store more context for smarter replies" },
                { icon: ListTodo, text: "Get help with planning and tasks" },
                { icon: Grid, text: "Explore projects, tasks, and more" },
            ]
        },
        {
            name: "Free",
            monthlyPrice: "0",
            annualPrice: "0",
            subtext: "See what AI can do",
            btnText: "Your current plan",
            btnVariant: "outline" as const,
            features: [
                { icon: Sparkles, text: "Get simple explanations" },
                { icon: MessageSquare, text: "Have short chats for common questions" },
                { icon: ImageIcon, text: "Try out limited image generation" },
                { icon: History, text: "Save limited memory and context" },
            ]
        }
    ];

    return (
        <div className={`fixed inset-0 z-50 ${overlayBg} backdrop-blur-sm flex items-center justify-center`}>
            <div className={`w-full max-w-[100vw] h-[100vh] border flex flex-col overflow-hidden relative shadow-2xl ${modalBg}`}>

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className={noTextSelect(`absolute top-4 right-4 transition-colors z-10 ${closeBtn}`)}
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Billing Period Toggle */}
                <div className="flex justify-center sm:my-6 my-3">
                    <div className={`inline-flex rounded-full p-1 border ${toggleBg}`}>
                        <button
                            type="button"
                            onClick={() => setBillingPeriod('monthly')}
                            className={noTextSelect(`px-6 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === 'monthly'
                                ? toggleActive
                                : toggleInactive
                                }`)}
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            onClick={() => setBillingPeriod('annually')}
                            className={noTextSelect(`px-6 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === 'annually'
                                ? toggleActive
                                : toggleInactive
                                }`)}
                        >
                            Annually
                        </button>
                    </div>
                </div>


                {/* Content */}
                <div className={`flex-1 w-full overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full ${scrollThumb}`}>

                    {limitReachedMessage && (
                        <div className="px-4 md:px-8 mb-3">
                            <div
                                className={cn(
                                    'max-w-[830px] mx-auto rounded-2xl border px-4 py-3 flex items-start gap-3 relative',
                                    isDarkMode ? 'border-red-500/30 bg-red-500/10 text-white' : 'border-red-200 bg-red-50 text-gray-900'
                                )}
                        >
                            <div className={cn('mt-0.5 rounded-xl p-2', isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600')}>
                                <Sparkles className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                                <div className="text-sm font-semibold">{limitReachedMessage}</div>
                                <div className={cn('text-xs mt-0.5', isDarkMode ? 'text-gray-300' : 'text-gray-600')}>
                                    Upgrade to Pro to unlock higher limits.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className={cn(
                                    'absolute top-2 right-2 p-1 rounded-full transition-colors',
                                    isDarkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                                )}
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
                    <div className="p-4 md:p-8 pt-0">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 items-stretch max-w-[830px] mx-auto justify-items-center">
                            {plans.map((plan, index) => {
                                const currentPrice = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
                                const oldPrice = plan.oldMonthlyPrice && plan.oldAnnualPrice
                                    ? (billingPeriod === 'monthly' ? plan.oldMonthlyPrice : plan.oldAnnualPrice)
                                    : undefined;
                                const period = billingPeriod === 'monthly' ? 'month' : 'year';
                                const isGo = plan.name === 'Pro';

                                const cardBg = isGo
                                    ? (isDarkMode ? 'bg-[#26284a] border-[#3a3d74]' : 'bg-[#F5F5FF] border-[#6a67ff]')
                                    : (isDarkMode ? 'bg-[#1a1a1b] border-[#2a2a2b]' : 'bg-white border-gray-400');

                                const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
                                const textMuted = isDarkMode ? 'text-gray-300' : 'text-gray-600';

                                const isCurrent = (plan.name === 'Free' && !isPro) || (plan.name === 'Pro' && isPro);
                                const btnText = isCurrent ? 'Your current plan' : (plan.name === 'Pro' ? 'Upgrade to Pro' : 'Free plan');
                                const disabled = isCurrent || plan.name === 'Free';

                                return (
                                    <div
                                        key={index}
                                        className={`w-full max-w-[400px] rounded-2xl border ${cardBg} p-5 sm:p-6 flex flex-col min-h-[500px]`}
                                    >
                                        {/* Card Header */}
                                        {/* Card Header */}
                                        <div className="flex items-start justify-between">
                                            <h3 className={`text-lg sm:text-xl font-semibold ${textPrimary}`}>{plan.name}</h3>
                                            {plan.specialOffer && (
                                                <span className={cn(
                                                    "text-[10px] px-2 py-1 rounded-full border tracking-wide",
                                                    isDarkMode
                                                        ? "bg-white/10 border-white/10 text-gray-100"
                                                        : "bg-[#E7E6FF] border-[#6a67ff]/30 text-[#2b2a7a]"
                                                )}>
                                                    SPECIAL OFFER
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-4">
                                            <div className="flex items-end gap-2 flex-wrap">
                                                {oldPrice && (
                                                    <span className={cn(
                                                        "text-2xl sm:text-3xl line-through",
                                                        isDarkMode ? "text-gray-300/70" : "text-gray-500"
                                                    )}>{oldPrice}</span>
                                                )}
                                                <span className={`text-4xl sm:text-5xl font-semibold leading-none ${textPrimary}`}>{currentPrice}</span>
                                                <span className={`text-xs sm:text-sm mb-1 ${textMuted}`}>INR / {period}</span>
                                            </div>
                                            <p key={isGo ? typewriterKey : undefined} className={`mt-4 font-semibold ${textPrimary}`}>{isGo ? proSubtextTyped : plan.subtext}</p>
                                        </div>

                                        <button
                                            type="button"
                                            className={noTextSelect(`mt-4 w-full h-11 rounded-full text-sm font-semibold transition-colors ${plan.btnVariant === 'outline'
                                                ? (isDarkMode
                                                    ? 'bg-white/10 text-gray-200 border border-white/10 cursor-default'
                                                    : 'bg-gray-100 text-gray-600 border border-gray-200 cursor-default')
                                                : plan.btnVariant === 'primary'
                                                    ? 'bg-[#6a67ff] hover:bg-[#5b58ff] text-white'
                                                    : (isDarkMode
                                                        ? 'bg-white text-black hover:bg-white/90'
                                                        : 'bg-black text-white hover:bg-black/90')
                                                }`)}
                                            disabled={disabled}
                                        >
                                            {btnText}
                                        </button>

                                        <div className="mt-5 space-y-3">
                                            {plan.features.map((feature, idx) => (
                                                <div key={idx} className="flex items-center gap-3">
                                                    <div className={cn("mt-0.5 rounded-md p-1", isDarkMode ? "text-gray-300" : "text-gray-500")}>
                                                        <feature.icon className="w-4 h-4" />
                                                    </div>
                                                    <div className={`${textPrimary} text-sm leading-snug`}>{feature.text}</div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-auto hidden pt-6">
                                            {plan.specialOffer && (
                                                <div className="text-xs text-white">
                                                    <span className="underline font-semibold"></span>{' '}
                                                    <span className="opacity-90"></span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Enterprise Footer - Hidden */}
                        <div className="mt-8 text-center">
                            <div className="text-gray-300 text-xs"></div>
                            <div className="text-gray-300 text-xs underline"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
